import { capture } from '@parcel/analytics/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type MembershipOutcome, runChunkedMembership } from './view-selection-state.js';
import {
  type WatchDataset,
  type WorkspaceView,
  invalidateWatchCaches,
  watchRecords,
} from './workspace-views.js';

/**
 * Follows a table selection in 100-id chunks as one write: caches refresh once at the end, and
 * the caller reports the outcomes (a failed chunk becomes failure outcomes, not a toast here).
 */
export function useWatchSelection(workspaceId: string | undefined, dataset: WatchDataset) {
  const queryClient = useQueryClient();
  const follow = useMutation({
    mutationFn: async (recordIds: readonly string[]) => {
      if (!workspaceId) throw new Error('Cannot follow without a workspace');
      let view: WorkspaceView | null = null;
      let changed = 0;
      const outcomes = await runChunkedMembership(recordIds, async (chunk) => {
        const out = await watchRecords(workspaceId, dataset, chunk);
        view = out.view ?? view;
        changed += out.changed;
        return out;
      });
      return { view: view as WorkspaceView | null, outcomes, changed };
    },
    onSettled: () => invalidateWatchCaches(queryClient, workspaceId, dataset),
  });
  return async (
    recordIds: readonly string[],
  ): Promise<{ view: WorkspaceView | null; outcomes: MembershipOutcome[] }> => {
    const { view, outcomes, changed } = await follow.mutateAsync(recordIds);
    if (changed > 0) {
      capture('record_followed', {
        record_type: dataset,
        followed: true,
        count: changed,
        source: 'toolbar',
      });
    }
    return { view, outcomes };
  };
}
